import { Button } from '@crowdex/ui/components/button';
import { Textarea } from '@crowdex/ui/components/textarea';
import { POPUP_SURFACE } from '@crowdex/ui/lib/surfaces';
import { FileCode2, ListChecks, MessageSquare, Terminal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';

type AnchoredPosition = {
  anchor: { x: number; y: number };
  range: Range | null;
  visible: boolean;
};

function rangePosition(range: Range): Omit<AnchoredPosition, 'range'> | null {
  const node = range.commonAncestorContainer;
  if (!node.isConnected) return null;
  const rect = range.getBoundingClientRect();
  let clipTop = 0;
  let clipRight = window.innerWidth;
  let clipBottom = window.innerHeight;
  let clipLeft = 0;
  let element = node instanceof Element ? node : node.parentElement;
  while (element !== null) {
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return { anchor: { x: rect.left, y: rect.bottom + 8 }, visible: false };
    }
    if (/(auto|scroll)/.test(`${style.overflow} ${style.overflowX} ${style.overflowY}`)) {
      const bounds = element.getBoundingClientRect();
      clipTop = Math.max(clipTop, bounds.top);
      clipRight = Math.min(clipRight, bounds.right);
      clipBottom = Math.min(clipBottom, bounds.bottom);
      clipLeft = Math.max(clipLeft, bounds.left);
    }
    element = element.parentElement;
  }
  return {
    anchor: { x: rect.left, y: rect.bottom + 8 },
    visible:
      rect.bottom >= clipTop &&
      rect.top <= clipBottom &&
      rect.right >= clipLeft &&
      rect.left <= clipRight,
  };
}

/**
 * Floating popover to attach a written comment to a chat/file/terminal
 * selection. Reads and writes the pending-annotation state straight from the
 * desktop shell store, so `DesktopShell` no longer drills that state down.
 */
export function PendingAnnotationPopover({ onFocusComposer }: { onFocusComposer: () => void }) {
  const pendingAnnotation = useDesktopShellStore((state) => state.pendingAnnotation);
  const pendingAnnotationText = useDesktopShellStore((state) => state.pendingAnnotationText);
  const annotationCount = useDesktopShellStore((state) => state.responseAnnotations.length);
  const setPendingAnnotation = useDesktopShellStore((state) => state.setPendingAnnotation);
  const setPendingAnnotationText = useDesktopShellStore((state) => state.setPendingAnnotationText);
  const setResponseAnnotations = useDesktopShellStore((state) => state.setResponseAnnotations);
  const [anchoredPosition, setAnchoredPosition] = useState<AnchoredPosition | null>(null);

  useEffect(() => {
    const range = pendingAnnotation?.anchorRange;
    if (pendingAnnotation === null || range === undefined) {
      setAnchoredPosition(null);
      return;
    }
    let frame: number | null = null;
    const update = (): void => {
      frame = null;
      const position = rangePosition(range);
      if (position === null) {
        setPendingAnnotation(null);
        return;
      }
      setAnchoredPosition({ ...position, range });
    };
    const scheduleUpdate = (): void => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, { attributes: true, childList: true, subtree: true });
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
      observer.disconnect();
    };
  }, [pendingAnnotation, setPendingAnnotation]);

  if (pendingAnnotation === null) return null;

  const position =
    pendingAnnotation.anchorRange !== undefined &&
    anchoredPosition?.range === pendingAnnotation.anchorRange
      ? anchoredPosition
      : { anchor: pendingAnnotation.anchor, range: null, visible: true };

  const submit = (): void => {
    if (pendingAnnotationText.trim().length === 0) return;
    setResponseAnnotations((current) => [
      ...current,
      {
        annotation: pendingAnnotationText.trim(),
        id: crypto.randomUUID(),
        kind: pendingAnnotation.sourceKind,
        source: pendingAnnotation.sourceLabel,
        text: pendingAnnotation.content,
      },
    ]);
    setPendingAnnotation(null);
    setPendingAnnotationText('');
    window.requestAnimationFrame(onFocusComposer);
  };

  return (
    <>
      <span
        className={`fixed z-popover flex size-9 items-center justify-center rounded-full border-2 border-white bg-blue-600 font-semibold text-sm text-white shadow-lg transition-opacity ${position.visible ? '' : 'pointer-events-none opacity-0'}`}
        style={{
          left: Math.max(12, Math.min(position.anchor.x - 18, window.innerWidth - 48)),
          top: Math.max(12, Math.min(position.anchor.y - 18, window.innerHeight - 48)),
        }}
      >
        {annotationCount + 1}
      </span>
      <div
        className={`${POPUP_SURFACE} fixed z-popover max-h-[calc(100dvh-1.5rem)] w-[min(22rem,calc(100vw-1.5rem))] overflow-y-auto rounded-xl p-3 transition-opacity ${position.visible ? '' : 'pointer-events-none opacity-0'}`}
        role="dialog"
        style={
          // No room beside its number on a phone: under it, so both stay in sight.
          window.innerWidth < 480
            ? {
                left: 12,
                top: Math.max(12, Math.min(position.anchor.y + 26, window.innerHeight - 248)),
              }
            : {
                left: Math.max(12, Math.min(position.anchor.x + 44, window.innerWidth - 364)),
                top: Math.max(12, Math.min(position.anchor.y, window.innerHeight - 248)),
              }
        }
      >
        <div className="mb-2 flex items-center gap-2 text-muted-foreground text-xs">
          {pendingAnnotation.sourceKind === 'terminal' ? (
            <Terminal className="size-3.5" />
          ) : pendingAnnotation.sourceKind === 'file' ? (
            <FileCode2 className="size-3.5" />
          ) : pendingAnnotation.sourceKind === 'plan' ? (
            <ListChecks className="size-3.5" />
          ) : (
            <MessageSquare className="size-3.5" />
          )}
          <span className="truncate">{pendingAnnotation.sourceLabel}</span>
        </div>
        <blockquote className="mb-2 line-clamp-3 whitespace-pre-wrap border-muted-foreground/40 border-l-2 pl-2 text-muted-foreground text-xs leading-5">
          {pendingAnnotation.content}
        </blockquote>
        <Textarea
          autoFocus
          className="min-h-20 resize-none"
          onChange={(event) => setPendingAnnotationText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setPendingAnnotation(null);
            } else if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder="Add a comment…"
          rows={3}
          value={pendingAnnotationText}
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button onClick={() => setPendingAnnotation(null)} size="sm" variant="ghost">
            Cancel
          </Button>
          <Button disabled={pendingAnnotationText.trim().length === 0} onClick={submit} size="sm">
            Add
          </Button>
        </div>
      </div>
    </>
  );
}
